import React, { useState, useEffect } from 'react';
import ChatHeader from './ChatHeader';
import ChatBody from './ChatBody';
import ChatInput from './ChatInput';
import SuggestionsBar from './SuggestionsBar';
import { ChatService } from '../services/chatService';
import { WELCOME_MESSAGE } from '../constants/messages';
import './ChatWidget.css';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [userId, setUserId] = useState(null);

  // Widget dimensions
  const defaultWidth = 450;
  const defaultHeight = 600;
  const maxWidth = 1000;
  const maxHeight = 700;

  // Listen for user ID from parent window
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'ENTERONE_USER_ID') {
        console.log('Chat Widget received user ID:', event.data.userId);
        // Store user ID for API requests
        setUserId(event.data.userId);
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Load welcome message - always start fresh on page load
  useEffect(() => {
    const loadWelcomeMessage = () => {
      // Set initial welcome message
      const welcomeMessage = {
        id: '1',
        type: 'bot',
        text: WELCOME_MESSAGE,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
      
      // Always start fresh on page load - action buttons will show
      // hasInteracted will be set to true only when user actually interacts
      setHasInteracted(false);
      
      // Initially, no suggestions (action buttons will show for fresh users)
      setCurrentSuggestions([]);
    };
    loadWelcomeMessage();
  }, []);

  const toggleWidget = () => {
    setIsOpen(!isOpen);
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  const handleSendMessage = (message) => {
    if (message.trim()) {
      // Mark as interacted on first message
      if (!hasInteracted) {
        setHasInteracted(true);
      }

      const newMessage = {
        id: Date.now().toString(),
        type: 'user',
        text: message,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, newMessage]);

      // Show typing indicator
      setIsTyping(true);

      // Get bot response based on user message - pass userId if available
      ChatService.sendMessage(message, userId).then(response => {
        setIsTyping(false);
        const botResponse = {
          id: (Date.now() + 1).toString(),
          type: 'bot',
          text: response.message,
          links: response.links,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botResponse]);
        
        // Update suggestions bar with new suggestions
        if (response.suggestions && response.suggestions.length > 0) {
          setCurrentSuggestions(response.suggestions);
        } else {
          setCurrentSuggestions([]);
        }
      }).catch(error => {
        setIsTyping(false);
        console.error('Error getting bot response:', error);
        // Fallback error message
        const errorResponse = {
          id: (Date.now() + 1).toString(),
          type: 'bot',
          text: "Sorry, I'm having trouble responding right now. Please try again.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponse]);
      });
    }
  };

  const handleActionClick = (action) => {
    // Mark as interacted when action button is clicked
    if (!hasInteracted) {
      setHasInteracted(true);
    }
    
    // Send the action as a user message
    handleSendMessage(action);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Fetch chat history from API
      const sessionId = 'chat-123'; // TODO: Update this to fetch dynamically later
      const history = await ChatService.getChatHistory(sessionId);
      
      if (history && history.length > 0) {
        // If history exists, display it
        setMessages(history);
        // User has interacted if there's history
        setHasInteracted(history.length > 1);
      } else {
        // If no history, show welcome message
        const welcomeMessage = {
          id: '1',
          type: 'bot',
          text: WELCOME_MESSAGE,
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
        setHasInteracted(false);
      }
      
      // Clear suggestions on refresh
      setCurrentSuggestions([]);
    } catch (error) {
      console.error('Error refreshing chat:', error);
      // On error, show welcome message
      const welcomeMessage = {
        id: '1',
        type: 'bot',
        text: WELCOME_MESSAGE,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
      setHasInteracted(false);
      setCurrentSuggestions([]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="chat-widget-container">
      {/* Floating Button */}
      <button 
        className={`chat-toggle-btn ${isOpen ? 'open' : ''}`}
        onClick={toggleWidget}
        aria-label="Toggle chat widget"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`chat-window ${isMaximized ? 'maximized' : ''}`}
          style={{
            width: isMaximized ? `${maxWidth}px` : `${defaultWidth}px`,
            height: isMaximized ? `${maxHeight}px` : `${defaultHeight}px`
          }}
        >
          <ChatHeader 
            onClose={toggleWidget} 
            onRefresh={handleRefresh}
            onMaximize={toggleMaximize}
            isMaximized={isMaximized}
          />
          <ChatBody 
            messages={messages} 
            onActionClick={handleActionClick}
            isRefreshing={isRefreshing}
            isTyping={isTyping}
            showActionButtons={!hasInteracted && messages.length === 1}
          />
          {/* Suggestions bar only appears after first interaction */}
          {hasInteracted && (
            <SuggestionsBar 
              suggestions={currentSuggestions}
              onSuggestionClick={handleSendMessage}
            />
          )}
          <ChatInput onSendMessage={handleSendMessage} />
        </div>
      )}
    </div>
  );
};

export default ChatWidget;

